import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DeviceDetectorService } from "ngx-device-detector";
import { FirebaseApp, getApps, initializeApp } from "firebase/app";
import { getId, getInstallations } from "firebase/installations";
import { firstValueFrom, Observable, of } from "rxjs";
import { catchError, timeout } from "rxjs/operators";
import { CookieService } from "ngx-cookie-service";
import { environment } from "../../../environments/environment";
import { LoginService, SKIP_ERROR_HANDLER_HEADER } from "./login.service";

interface DeviceRegistryPayload {
  device_id?: string;
  os?: string;
  os_version?: string;
  app_version?: string;
  device_model?: string;
  language?: string;
  logout?: boolean;
}

interface DeviceRegistryResponse {
  id: number | string;
  device_id?: string;
}

interface FirebaseWebConfig {
  apiKey: string;
  appId: string;
  projectId: string;
  messagingSenderId?: string;
  authDomain?: string;
  storageBucket?: string;
  measurementId?: string;
}

@Injectable()
export class DeviceRegistryService {
  private readonly registerUrl: string = `${environment.urlbase}devices/register`;
  private readonly syncUrl: string = `${environment.urlbase}devices/sync`;

  private readonly storageNamespace: string = this.resolveStorageNamespace();
  private readonly internalIdKey: string = `${this.storageNamespace}_device_registry_internal_id`;
  private readonly vendorIdKey: string = `${this.storageNamespace}_device_registry_vendor_id`;
  private readonly vendorIdFallbackKey: string = `${this.storageNamespace}__app_vendor_id`;
  private readonly internalIdFallbackKey: string = `${this.storageNamespace}__app_internal_device_id`;
  private readonly runtimeFirebaseConfigPath: string = "/assets/runtime/firebase-config.json";
  private readonly legacyFirebaseConfigPath: string = "/assets/js/checkout/analytics.js";
  private readonly firebaseAppName: string = "edm-device-registry";

  private vendorIdPromise: Promise<string> | null = null;
  private firebaseApp: FirebaseApp | null = null;

  constructor(
    private http: HttpClient,
    private deviceService: DeviceDetectorService,
    private loginService: LoginService,
    private cookieService: CookieService
  ) {}

  async bootstrap(): Promise<void> {
    await this.ensureXsrfCookie();

    if (this.getInternalDeviceId()) {
      this.sync().subscribe();
      return;
    }

    try {
      await this.ensureRegistered(3000);
    } catch (_error) {
      // Fallback resiliente: não bloqueia uso do app.
    }
  }

  getInternalDeviceId(): string {
    const id = localStorage.getItem(this.internalIdKey) || localStorage.getItem(this.internalIdFallbackKey);
    return this.isValid(id) ? id : "";
  }

  getOrCreateVendorDeviceId(): string {
    const stored = localStorage.getItem(this.vendorIdKey) || localStorage.getItem(this.vendorIdFallbackKey);
    if (this.isValid(stored)) {
      return stored;
    }
    const generated = this.generateDeviceId();
    this.storeVendorDeviceId(generated);
    return generated;
  }

  /**
   * Garante que o cookie XSRF-TOKEN exista antes do primeiro POST.
   *
   * O backend (Laravel) só emite o cookie em uma resposta do grupo stateful.
   * No primeiro acesso, `devices/register` é a primeira requisição do app: sem
   * o cookie, o WSInterceptor não envia o header X-XSRF-TOKEN e o POST falha com
   * "CSRF token mismatch". Um GET prévio resolve — GET não passa pelo
   * VerifyCsrfToken, e até um 401 (dispositivo não autenticado) entrega o
   * Set-Cookie. Pulamos a chamada quando o cookie já está presente.
   */
  private async ensureXsrfCookie(timeoutMs = 3000): Promise<void> {
    if (this.cookieService.check("XSRF-TOKEN")) {
      return;
    }

    const headers = new HttpHeaders().set(SKIP_ERROR_HANDLER_HEADER, "true");
    try {
      await firstValueFrom(
        this.http
          .get(environment.urlbase + "user", { headers })
          .pipe(timeout(timeoutMs))
      );
    } catch (_error) {
      // Ignorado de propósito: o objetivo é apenas o Set-Cookie do XSRF-TOKEN.
    }
  }

  async ensureRegistered(timeoutMs = 3000): Promise<string> {
    const existing = this.getInternalDeviceId();
    if (existing) {
      return existing;
    }

    const payload = await this.buildBasePayload(true);
    const request$ = this.http
      .post<DeviceRegistryResponse>(this.registerUrl, payload)
      .pipe(timeout(timeoutMs));
    let response: DeviceRegistryResponse;
    try {
      response = await firstValueFrom(request$);
    } catch (error) {
      const err = error as HttpErrorResponse;
      if (this.shouldRefreshDeviceRegistration(err)) {
        this.clearInternalDeviceId();
      }
      throw error;
    }
    const internalId = String(response?.id || "");
    if (!this.isValid(internalId)) {
      throw new Error("Device register sem id.");
    }
    this.storeInternalDeviceId(internalId);
    if (this.isValid(response?.device_id)) {
      this.storeVendorDeviceId(String(response.device_id));
    }
    return internalId;
  }

  sync(extraPayload: DeviceRegistryPayload = {}): Observable<DeviceRegistryResponse | null> {
    const internalId = this.getInternalDeviceId();
    if (!internalId) {
      return of(null);
    }

    const request$ = async (): Promise<DeviceRegistryResponse | null> => {
      const payload: DeviceRegistryPayload = {
        ...(await this.buildBasePayload(false)),
        ...extraPayload,
      };
      const headers = new HttpHeaders().set("X-Device-Id", internalId);
      try {
        return await firstValueFrom(this.http.post<DeviceRegistryResponse>(this.syncUrl, payload, { headers }));
      } catch (error) {
        const err = error as HttpErrorResponse;
        if (this.shouldRefreshDeviceRegistration(err)) {
          this.clearInternalDeviceId();
          this.ensureRegistered(3000).catch(() => null);
        }
        return null;
      }
    };

    return new Observable<DeviceRegistryResponse | null>((subscriber) => {
      request$()
        .then((response) => {
          subscriber.next(response);
          subscriber.complete();
        })
        .catch(() => {
          subscriber.next(null);
          subscriber.complete();
        });
    }).pipe(catchError(() => of(null)));
  }

  private async buildBasePayload(includeDeviceId: boolean): Promise<DeviceRegistryPayload> {
    const deviceInfo = this.deviceService.getDeviceInfo();
    const payload: DeviceRegistryPayload = {
      os: "web",
      os_version: deviceInfo?.os_version || "",
      app_version: this.loginService.version || "web",
      device_model: deviceInfo?.device === "unknown" ? deviceInfo?.browser || "web" : deviceInfo?.device || "web",
      language: this.normalizeLanguage(navigator?.language),
    };

    if (includeDeviceId) {
      payload.device_id = await this.getOrCreateFirebaseVendorDeviceId();
    } else {
      payload.device_id = this.getStoredVendorDeviceId();
    }

    return payload;
  }

  private async getOrCreateFirebaseVendorDeviceId(): Promise<string> {
    const stored = this.getStoredVendorDeviceId();
    if (stored) {
      return stored;
    }

    if (!this.vendorIdPromise) {
      this.vendorIdPromise = this.resolveFirebaseInstallationId()
        .then((fid) => {
          if (fid) {
            this.storeVendorDeviceId(fid);
            return fid;
          }
          const fallbackId = this.generateDeviceId();
          this.storeVendorDeviceId(fallbackId);
          return fallbackId;
        })
        .finally(() => {
          this.vendorIdPromise = null;
        });
    }

    return this.vendorIdPromise;
  }

  private getStoredVendorDeviceId(): string {
    const stored = localStorage.getItem(this.vendorIdKey) || localStorage.getItem(this.vendorIdFallbackKey);
    return this.isValid(stored) ? String(stored) : "";
  }

  private async resolveFirebaseInstallationId(): Promise<string | null> {
    const firebaseConfig = await this.resolveFirebaseConfig();
    if (!firebaseConfig) {
      return null;
    }

    try {
      const app = this.getOrCreateFirebaseApp(firebaseConfig);
      const installations = getInstallations(app);
      const installationId: string = await getId(installations);
      return this.isValid(installationId) ? installationId : null;
    } catch (_error) {
      return null;
    }
  }

  private async resolveFirebaseConfig(): Promise<FirebaseWebConfig | null> {
    const configFromWindow = this.getFirebaseConfigFromWindow();
    if (configFromWindow) {
      return configFromWindow;
    }

    const configUrls = this.getFirebaseConfigCandidateUrls();
    for (const configUrl of configUrls) {
      try {
        const response = await fetch(configUrl, { cache: "no-store" });
        if (!response.ok) {
          continue;
        }
        const content = await response.text();
        const parsedConfig = this.extractFirebaseConfig(content);
        if (parsedConfig) {
          return parsedConfig;
        }
      } catch (_error) {
        // Continua tentando próximos endpoints.
      }
    }

    return null;
  }

  private getFirebaseConfigFromWindow(): FirebaseWebConfig | null {
    const globalWindow = window as Window & { __EDM_FIREBASE_CONFIG?: FirebaseWebConfig };
    const config = globalWindow.__EDM_FIREBASE_CONFIG;
    if (!config) {
      return null;
    }
    return this.isValidFirebaseConfig(config) ? config : null;
  }

  private getFirebaseConfigCandidateUrls(): string[] {
    const envConfig = environment as Record<string, unknown>;
    const globalWindow = window as Window & {
      __EDM_FIREBASE_CONFIG_URL?: string;
      __EDM_FIREBASE_CONFIG_S3_URL?: string;
    };

    const candidates = [
      globalWindow.__EDM_FIREBASE_CONFIG_URL,
      globalWindow.__EDM_FIREBASE_CONFIG_S3_URL,
      this.readString(envConfig.firebaseConfigS3Url),
      this.readString(envConfig.firebaseConfigUrl),
      this.readString(envConfig.firebaseInstallationsConfigUrl),
      this.runtimeFirebaseConfigPath,
      this.legacyFirebaseConfigPath,
    ];

    const deduped = new Set<string>();
    for (const candidate of candidates) {
      if (this.isValid(candidate)) {
        deduped.add(String(candidate).trim());
      }
    }
    return Array.from(deduped);
  }

  private extractFirebaseConfig(content: string): FirebaseWebConfig | null {
    const fromJson = this.parseFirebaseConfigFromJson(content);
    if (fromJson) {
      return fromJson;
    }

    const match = content.match(/const\s+firebaseConfig\s*=\s*({[\s\S]*?});/m);
    if (match && match[1]) {
      try {
        const parsed = new Function(`return (${match[1]});`)() as FirebaseWebConfig;
        return this.isValidFirebaseConfig(parsed) ? parsed : null;
      } catch (_error) {
        return null;
      }
    }

    const windowMatch = content.match(/__EDM_FIREBASE_CONFIG\s*=\s*({[\s\S]*?});/m);
    if (windowMatch && windowMatch[1]) {
      try {
        const parsed = new Function(`return (${windowMatch[1]});`)() as FirebaseWebConfig;
        return this.isValidFirebaseConfig(parsed) ? parsed : null;
      } catch (_error) {
        return null;
      }
    }

    return null;
  }

  private parseFirebaseConfigFromJson(content: string): FirebaseWebConfig | null {
    try {
      const parsedJson = JSON.parse(content) as
        | FirebaseWebConfig
        | { firebaseConfig?: FirebaseWebConfig; firebase?: FirebaseWebConfig };
      const parsed =
        (parsedJson as { firebaseConfig?: FirebaseWebConfig })?.firebaseConfig ||
        (parsedJson as { firebase?: FirebaseWebConfig })?.firebase ||
        (parsedJson as FirebaseWebConfig);
      return this.isValidFirebaseConfig(parsed) ? parsed : null;
    } catch (_error) {
      return null;
    }
  }

  private isValidFirebaseConfig(config: FirebaseWebConfig): boolean {
    return this.isValid(config?.apiKey) && this.isValid(config?.appId) && this.isValid(config?.projectId);
  }

  private getOrCreateFirebaseApp(config: FirebaseWebConfig): FirebaseApp {
    if (this.firebaseApp) {
      return this.firebaseApp;
    }
    const existingApp = getApps().find((app) => app.name === this.firebaseAppName);
    this.firebaseApp = existingApp || initializeApp(config, this.firebaseAppName);
    return this.firebaseApp;
  }

  private storeInternalDeviceId(id: string): void {
    localStorage.setItem(this.internalIdKey, id);
    localStorage.setItem(this.internalIdFallbackKey, id);
  }

  private clearInternalDeviceId(): void {
    localStorage.removeItem(this.internalIdKey);
    localStorage.removeItem(this.internalIdFallbackKey);
  }

  private shouldRefreshDeviceRegistration(error: HttpErrorResponse): boolean {
    return error.status === 404 || error.status === 419;
  }

  private storeVendorDeviceId(id: string): void {
    localStorage.setItem(this.vendorIdKey, id);
    localStorage.setItem(this.vendorIdFallbackKey, id);
  }

  private generateDeviceId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return `web-${Date.now()}-${Math.floor(Math.random() * 1000000000)}`;
  }

  private isValid(value: unknown): boolean {
    return value !== null && value !== undefined && value !== "";
  }

  private readString(value: unknown): string {
    return typeof value === "string" ? value : "";
  }

  private normalizeLanguage(value: unknown): string {
    const fallback = "pt-BR";
    const raw = this.readString(value).trim();
    const language = this.isValid(raw) ? raw : fallback;
    return language.slice(0, 10);
  }

  private resolveStorageNamespace(): string {
    const envConfig = environment as Record<string, unknown>;
    const appName = this.readString(envConfig.app_name) || this.readString(envConfig.appname);
    const normalized = appName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return this.isValid(normalized) ? normalized : "default";
  }
}
